import { catalogValue, type CatalogRow, type CatalogFacet } from './geminiCatalogFilters.ts';
import { UNSPECIFIED, accentLabel, countryGroupLabel, record, regionLabel } from './voiceLabels.ts';

export type CatalogAccent = { region_code: string; accent: string };
export type CatalogAccentGroup = { country: string; label: string; accents: { value: string; label: string }[] };
const specified = (label: string) => label === UNSPECIFIED ? '' : label;
const safePart = (value: unknown): value is string => typeof value === 'string' && value === value.trim() && value.length <= 120 && !/[<>\u0000-\u001f]/.test(value);

export const catalogAccentKey = ({ region_code, accent }: CatalogAccent) => JSON.stringify([region_code, accent]);
export const UNSPECIFIED_ACCENT = catalogAccentKey({ region_code: '', accent: '' });
export const countryAccentFilterKey = (country: string) => JSON.stringify([country, null]);
export function catalogAccent(voice: CatalogRow): CatalogAccent {
  return { region_code: specified(catalogValue(voice, 'region_code')), accent: specified(catalogValue(voice, 'accent')) };
}
export const catalogAccentValue = (voice: CatalogRow) => catalogAccentKey(catalogAccent(voice));
export function catalogAccentLabel(voice: CatalogRow): string {
  const pair = catalogAccent(voice);
  return !pair.region_code && !pair.accent ? UNSPECIFIED : `${countryGroupLabel(pair.region_code)} > ${pair.accent || UNSPECIFIED}`;
}
export function readCatalogAccent(value: string): CatalogAccent {
  let pair: unknown;
  try { pair = JSON.parse(value); } catch { throw Error('Invalid accent selection.'); }
  if (!Array.isArray(pair) || pair.length !== 2 || !pair.every(safePart)) throw Error('Invalid accent selection.');
  return { region_code: pair[0], accent: pair[1] };
}
/** Country-only filters include all its accents; leaf filters match both fields.
 * The stored fields remain compatible with front-end geography checkboxes. */
export function catalogAccentFilters(value: string): Partial<Record<CatalogFacet, string>> {
  if (!value) return {};
  let pair: unknown;
  try { pair = JSON.parse(value); } catch { throw Error('Invalid accent filter.'); }
  if (!Array.isArray(pair) || pair.length !== 2 || !safePart(pair[0]) || (pair[1] !== null && !safePart(pair[1]))) throw Error('Invalid accent filter.');
  return { region_code: pair[0] || UNSPECIFIED, ...(pair[1] === null ? {} : { accent: pair[1] || UNSPECIFIED }) };
}

export function catalogAccentGroups(voices: readonly CatalogRow[], includeProviderDefaults = false): CatalogAccentGroup[] {
  const countries = new Map<string, Set<string>>();
  function add(pair: CatalogAccent) {
    const accents = countries.get(pair.region_code) ?? new Set<string>();
    accents.add(pair.accent); countries.set(pair.region_code, accents);
  }
  for (const voice of voices) {
    add(catalogAccent(voice));
    // A correction must not make the original provider choice disappear from
    // the editor. It is offered as a choice, never automatically restored.
    const source = record(voice.provider_metadata?.catalog);
    if (includeProviderDefaults && Object.keys(source).length) add({
      region_code: specified(regionLabel(source.region_code)), accent: specified(accentLabel(source.accent, source.region_code)),
    });
  }
  return [...countries].map(([country, accents]) => ({
    country, label: countryGroupLabel(country),
    accents: [...accents].map(accent => ({ value: catalogAccentKey({ region_code: country, accent }), label: `${countryGroupLabel(country)} > ${accent || UNSPECIFIED}` }))
      .sort((left, right) => left.label.localeCompare(right.label)),
  })).sort((left, right) => left.label.localeCompare(right.label) || left.country.localeCompare(right.country));
}
